import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { darkTheme } from "@yleisradio/yds-core";
import styled from "styled-components";

import { useEmbedParametersContext } from "../../contexts/embedParametersContext";
import { UI_SIZE } from "../../enums";
import { getAreenaUrl, getShareUrl } from "../../helpers/areenaLinks";
import { writeToClipboard } from "../../helpers/clipboard";
import { secondsToHHMMSSFormat } from "../../helpers/timeHelpers";
import i18n from "../../service/i18n";
import { usePlayerStore } from "../../store/usePlayerStore";
import { visibilityTransition } from "../../styles/commonStyles";
import { FadingComponent } from "../FadingComponent";

import ShareEmbedCode from "./ShareEmbedCode";
import ShareFrom from "./ShareFrom";
import ShareOverlayButton, {
  ShareOverlayButtonRef
} from "./ShareOverlayButton";
import SocialMediaLinks from "./SocialMediaLinks";

interface Props {
  closeShareOverlay?: () => void;
  isContentProtected: boolean;
  openShareOverlay?: () => void;
}

const ShareOverlayWrapper = styled.div<{ visible: boolean }>`
  align-items: center;
  background-color: ${darkTheme.BACKGROUND_DIALOG_OVERLAY};
  color: ${darkTheme.TEXT_DEFAULT};
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 200;
  ${props => visibilityTransition(props.visible)}
`;

const ShareOverlayTitle = styled.span<{
  size: UI_SIZE;
}>`
  font-size: 22px;
  font-weight: bold;
  margin-bottom: ${props =>
    props.size === UI_SIZE.SMALL || props.size === UI_SIZE.XSMALL
      ? "15px"
      : "32px"};
`;

const ShareOverlay = ({
  closeShareOverlay,
  isContentProtected,
  openShareOverlay
}: Props) => {
  const { subscribe, useStoreState } = usePlayerStore();
  const programId = useStoreState.programId();
  const isSimulcast = useStoreState.isSimulcast();
  const isControlsVisible = useStoreState.isControlsVisible();
  const isLive = useStoreState.isLive();
  const uiSize = useStoreState.uiSize();

  const { language } = useEmbedParametersContext();

  const embedCode = useRef<HTMLInputElement>();
  const shareOverlayRef = useRef(null);
  const overlayButtonsRef = useRef<ShareOverlayButtonRef>(null);

  const [addSeek, setAddSeek] = useState<boolean>(false);
  const [cursorOverButton, setCursorOverButton] = useState<boolean>(false);
  const [embedCodeVisible, setEmbedCodeVisible] = useState<boolean>(false);
  const [linkVisible, setLinkVisible] = useState<boolean>(false);
  const [overlayTransition, setOverlayTransition] = useState<boolean>(false);
  const [overlayVisible, setOverlayVisible] = useState<boolean>(false);
  const [progressInSeconds, setProgressInSeconds] = useState<number>(0);
  const [secondsSetByUser, setSecondsSetByUser] = useState<number | undefined>(
    undefined
  );
  const [shareTooltipVisible, setShareTooltipVisible] =
    useState<boolean>(false);

  const openOverlay = useCallback(
    (
      e:
        | React.MouseEvent<HTMLButtonElement, MouseEvent>
        | React.KeyboardEvent<HTMLButtonElement>
    ): void => {
      e.stopPropagation();
      openShareOverlay?.();
      setCursorOverButton(false);
      setOverlayVisible(true);
      setTimeout(() => {
        overlayButtonsRef.current?.closeButton?.focus();
      });
    },
    [openShareOverlay]
  );

  const closeOverlay = useCallback((): void => {
    setCursorOverButton(false);
    setOverlayVisible(false);
    setSecondsSetByUser(undefined);
    closeShareOverlay?.();
    setTimeout(() => {
      overlayButtonsRef.current?.openButton?.focus();
    });
  }, [closeShareOverlay]);

  const copyEmbedCode = () => {
    setTimeout(() => {
      writeToClipboard(embedCode.current).then(() => {
        setShareTooltipVisible(visible => !visible);
        setTimeout(() => {
          setShareTooltipVisible(visible => !visible);
        }, 1500);
      });
    }, 100); // Timeout for element to become visible or change
  };

  const checkboxToggle = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setAddSeek((e.target as HTMLInputElement).checked);
    copyEmbedCode();
  };

  const toggleEmbedCode = (
    e:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | KeyboardEvent<HTMLButtonElement>
  ): void => {
    e.preventDefault();
    setEmbedCodeVisible(visible => !visible);
    if (!embedCodeVisible) {
      setLinkVisible(false);
      copyEmbedCode();
    }
  };

  const toggleLink = (
    e:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | KeyboardEvent<HTMLButtonElement>
  ): void => {
    e.preventDefault();
    setLinkVisible(visible => !visible);
    if (!linkVisible) {
      setEmbedCodeVisible(false);
      copyEmbedCode();
    }
  };

  const shareFromChanged = (e: React.FocusEvent<HTMLInputElement>): void => {
    const seconds = Number(
      (e.target as HTMLInputElement).value
        .split(":")
        .map(Number)
        .reduce((acc: number, time: number) => {
          return 60 * acc + +time;
        })
    );

    if (Number.isInteger(seconds)) {
      setSecondsSetByUser(seconds);
    }
  };

  const onKeyDown = useCallback(
    event => {
      if (overlayVisible && event.key === "Escape") {
        closeOverlay();
      }
    },
    [closeOverlay, overlayVisible]
  );

  useEffect(() => {
    if (!overlayVisible) return;

    const unsubscribe = subscribe(
      state => state.currentTime,
      currentTime => {
        setProgressInSeconds(currentTime);
      },
      {
        fireImmediately: true
      }
    );

    return () => {
      unsubscribe();
    };
  }, [overlayVisible, subscribe]);

  useEffect(() => {
    if (overlayVisible && !overlayTransition) {
      setTimeout(() => {
        setOverlayTransition(true);
      });
    }
    if (!overlayVisible && overlayTransition) {
      setOverlayTransition(false);
    }
  }, [overlayTransition, overlayVisible]);

  const getShareLink = useCallback(
    ({ shareFromInSeconds, url, utmSource }) => {
      if (!programId) return "";
      return getShareUrl({
        language,
        programId,
        seek: addSeek ? shareFromInSeconds : undefined,
        socialMediaUrl: url,
        utmSource
      });
    },
    [addSeek, language, programId]
  );

  const buttonVisible = isControlsVisible || cursorOverButton || overlayVisible;
  const embedCodeAreaVisible = embedCodeVisible || linkVisible;

  const shareFromInSeconds = secondsSetByUser || Math.round(progressInSeconds);

  const jaaOhjelma = i18n("jaa_ohjelma", { language });

  const areenaLink = programId
    ? getAreenaUrl({
        isSimulcast,
        language,
        params:
          addSeek && Number.isInteger(shareFromInSeconds)
            ? { seek: shareFromInSeconds.toString() }
            : {},
        programId
      })
    : "";

  return (
    <div
      onBlur={event => {
        if (
          !event.currentTarget.contains(event.relatedTarget) &&
          overlayVisible
        ) {
          closeOverlay();
        }
      }}
      onFocus={event => {
        event.stopPropagation();
      }}
      onKeyDown={onKeyDown}
      ref={shareOverlayRef}
      tabIndex={-1}>
      <ShareOverlayButton
        ref={overlayButtonsRef}
        buttonVisible={buttonVisible}
        closeLabel={i18n("sulje_ohjelmanjakonäkymä", { language })}
        closeLinkOnClick={e => {
          e.preventDefault();
          closeOverlay();
        }}
        label={jaaOhjelma}
        onMouseEnter={() => setCursorOverButton(true)}
        onMouseLeave={() => setCursorOverButton(false)}
        openLinkOnClick={openOverlay}
        overlayVisible={overlayVisible}
        uiSize={uiSize}
      />
      <FadingComponent isVisible={overlayVisible}>
        <ShareOverlayWrapper role="dialog" visible={overlayTransition}>
          <ShareOverlayTitle size={uiSize}>{jaaOhjelma}</ShareOverlayTitle>
          <SocialMediaLinks
            shareFromInSeconds={shareFromInSeconds}
            language={language}
            getShareUrl={getShareLink}
            isContentProtected={isContentProtected}
            toggleEmbedCode={toggleEmbedCode}
            toggleLink={toggleLink}
            uiSize={uiSize}
            jaaOhjelma={jaaOhjelma}
          />
          <ShareEmbedCode
            embedCodeAreaVisible={embedCodeAreaVisible}
            shareTooltipVisible={shareTooltipVisible}
            embedCodeVisible={embedCodeVisible}
            language={language}
            shareFromInSeconds={shareFromInSeconds}
            addSeek={addSeek}
            areenaLink={areenaLink}
            setEmbedCode={code => (embedCode.current = code)}
            id={programId ?? ""}
            uiSize={uiSize}
          />
          {!isLive && (
            <ShareFrom
              language={language}
              shareFromInput={secondsToHHMMSSFormat(shareFromInSeconds)}
              checkboxToggle={checkboxToggle}
              shareFromChanged={shareFromChanged}
            />
          )}
        </ShareOverlayWrapper>
      </FadingComponent>
    </div>
  );
};

export default ShareOverlay;
